import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { formatDateTime, cn } from '../../lib/utils';
import { Plus, Image, Tag, Heart, MessageCircle, Camera, X } from 'lucide-react';
import { toast } from 'sonner';

const DiaryPage = () => {
  const { api, user, isFamily } = useAuth();
  const [posts, setPosts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [newPost, setNewPost] = useState({
    participant_id: '',
    text: '',
    tags: [],
    photos: [],
    visibility: 'internal'
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedParticipant]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, participantsRes] = await Promise.all([
        api.get('/diary', { 
          params: selectedParticipant !== 'all' ? { participant_id: selectedParticipant } : {}
        }),
        api.get('/participants')
      ]);
      setPosts(postsRes.data);
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error('Failed to load diary posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.participant_id || !newPost.text) {
      toast.error('Please select a participant and enter text');
      return;
    }

    try {
      await api.post('/diary', newPost);
      toast.success('Diary post created');
      setCreateDialogOpen(false);
      setNewPost({
        participant_id: '',
        text: '',
        tags: [],
        photos: [],
        visibility: 'internal'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleAddTag = () => {
    if (tagInput && !newPost.tags.includes(tagInput)) {
      setNewPost({ ...newPost, tags: [...newPost.tags, tagInput] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setNewPost({ ...newPost, tags: newPost.tags.filter(t => t !== tag) });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/diary/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewPost({ ...newPost, photos: [...newPost.photos, res.data.url] });
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const tagCategories = ['Community', 'Meals', 'Skills', 'Mood', 'Health', 'Activities', 'Social', 'Outdoors'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="diary-page">
      <PageHeader 
        title="Daily Diary"
        description="Share daily updates and moments with families"
        actions={
          !isFamily && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-post-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Diary Post</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Participant *</Label>
                    <Select 
                      value={newPost.participant_id} 
                      onValueChange={(v) => setNewPost({...newPost, participant_id: v})}
                    >
                      <SelectTrigger data-testid="post-participant">
                        <SelectValue placeholder="Select participant" />
                      </SelectTrigger>
                      <SelectContent>
                        {participants.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.first_name} {p.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>What's happening today? *</Label>
                    <Textarea 
                      value={newPost.text}
                      onChange={(e) => setNewPost({...newPost, text: e.target.value})}
                      placeholder="Share an update about the participant's day..."
                      rows={4}
                      data-testid="post-text"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {tagCategories.map(tag => (
                        <Badge 
                          key={tag}
                          variant={newPost.tags.includes(tag) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            if (newPost.tags.includes(tag)) {
                              handleRemoveTag(tag);
                            } else {
                              setNewPost({ ...newPost, tags: [...newPost.tags, tag] });
                            }
                          }}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Photos</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <label className="cursor-pointer">
                          <Camera className="h-4 w-4 mr-2" />
                          Upload Photo
                          <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            onChange={handlePhotoUpload}
                          />
                        </label>
                      </Button>
                    </div>
                    {newPost.photos.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {newPost.photos.map((url, idx) => (
                          <div key={idx} className="relative">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="h-16 w-16 object-cover rounded" />
                            <Button 
                              variant="destructive" 
                              size="icon" 
                              className="absolute -top-2 -right-2 h-5 w-5"
                              onClick={() => setNewPost({...newPost, photos: newPost.photos.filter((_, i) => i !== idx)})}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Visibility</Label>
                    <Select 
                      value={newPost.visibility} 
                      onValueChange={(v) => setNewPost({...newPost, visibility: v})}
                    >
                      <SelectTrigger data-testid="post-visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal Only</SelectItem>
                        <SelectItem value="family">Visible to Family</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreatePost} data-testid="save-post-btn">Post</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {/* Filter */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Participant</Label>
              <Select value={selectedParticipant} onValueChange={setSelectedParticipant}>
                <SelectTrigger className="w-48" data-testid="diary-participant-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Participants</SelectItem>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Posts Feed */}
      <div className="space-y-6 max-w-2xl mx-auto">
        {posts.length === 0 ? (
          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No diary posts yet</p>
            </CardContent>
          </Card>
        ) : (
          posts.map(post => (
            <Card key={post.id} className="border-border/40 bg-card/50 backdrop-blur-sm overflow-hidden" data-testid={`diary-post-${post.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {post.participant_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold">{post.participant_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Posted by {post.author_name} • {formatDateTime(post.created_at)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className={cn(
                    post.visibility === 'family' && 'bg-pink-500/10 text-pink-500 border-pink-500/30',
                    post.visibility === 'internal' && 'bg-slate-500/10 text-slate-500 border-slate-500/30',
                    post.visibility === 'public' && 'bg-green-500/10 text-green-500 border-green-500/30'
                  )}>
                    {post.visibility}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-foreground whitespace-pre-wrap">{post.text}</p>
                
                {post.photos?.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {post.photos.map((url, idx) => (
                      <img 
                        key={idx}
                        src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                        alt=""
                        className="rounded-lg object-cover w-full h-48"
                      />
                    ))}
                  </div>
                )}

                {post.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default DiaryPage;
